import { Injectable } from '@angular/core';
import Anthropic from '@anthropic-ai/sdk';
import { Observable, from, map, catchError, of, throwError } from 'rxjs';
import {
	DocumentBlockParam,
	TextBlock,
	TextBlockParam,
} from '@anthropic-ai/sdk/resources/index.mjs';
import { AIMessage } from '@meta-press/shared-plugins';
import { AIModel } from '../model/AIModel';
import { AIAPIResponse } from '../model/AIAPIResponse';
import { ThinkingQuality } from '../model/AIModelDefinition';

export interface ClaudeAPIResponse {
	rawResponse: string;
	json: any;
}

@Injectable({
	providedIn: 'root',
})
export class ClaudeAIService {
	private client: Anthropic;

	constructor() {
		// console.log('Claude AI Service not implemented.');
		this.client = new Anthropic({
			apiKey: '',
			defaultHeaders: {
				'Access-Control-Allow-Origin': 'http://localhost:4200',
				'Access-Control-Allow-Methods': 'GET,PUT,OPTIONS',
				'Access-Control-Allow-Headers':
					'Access-Control-Allow-Origin, Content-Type, Accept, Accept-Language, Origin, User-Agent',
			},
			dangerouslyAllowBrowser: true,
		});
	}

	public GetImageMetadata(file: File): Observable<any> {
		return throwError(
			() =>
				new Error(
					'Claude AI does not support image metadata extraction yet',
				),
		);
	}

	public getPromptCompletion(
		messages: AIMessage[],
		model: AIModel,
		thinkingQuality: ThinkingQuality = 'none',
	): Observable<AIAPIResponse> {
		console.error('Claude AI Service not implemented.');
		// Convert OpenAI message format to Claude format
		const claudeMessages = messages
			// .filter(msg => msg.role === 'user' || msg.role === 'assistant')
			.map((msg) => {
				let role = '';
				switch (msg.role) {
					case 'user':
						{
							role = 'user';
						}
						break;
					case 'assistant':
						{
							role = 'assistant';
						}
						break;

					case 'system': {
						role = 'user';
					}
				}

				switch (msg.type) {
					default:
					case 'text':
						{
							const obj: any = {
								role: role as 'user' | 'assistant',
								content: [
									{
										type: 'text',
										text: msg.content,
									} as TextBlockParam,
								],
							};

							if (msg.cache) {
								obj.content[0].cache_control = {
									type: 'ephemeral',
								};
							}

							return obj;
						}

						break;

					case 'document':
						{
							const obj: any = {
								role: msg.role as 'user' | 'assistant',
								content: [
									{
										type: 'document',
										source: {
											media_type: 'application/pdf',
											type: 'base64',
											data: msg.content,
										},
									} as DocumentBlockParam,
								],
							};

							if (msg.cache) {
								obj.content[0].cache_control = {
									type: 'ephemeral',
								};
							}

							return obj;
						}

						break;
						throw new Error('Unknown message type');
				}
			});

		const tmp: string = AIModel[model];

		return from(
			this.client.messages.create(
				{
					model: AIModel[model],
					max_tokens: 128000,
					messages: claudeMessages,
				},
				{
					headers: { 'anthropic-beta': 'output-128k-2025-02-19' },
				},
			),
		).pipe(
			map((response) => {
				console.log(response);
				let jsonString = (response.content[0] as TextBlock).text;

				try {
					// If claude returns the results surrounded by ```json, remove those before parsing
					// Find index of ```json
					const jsonIndex = jsonString.indexOf('```json');

					if (jsonIndex > -1) {
						// There might be some response text before the first ```json. Remove everything before it, including the ```json
						jsonString = jsonString.substring(
							jsonIndex + '```json'.length,
						);

						// Find the index of the ending ```
						const jsonEndIndex = jsonString.indexOf('```');

						if (jsonEndIndex > -1) {
							// Remove the ending ```
							jsonString = jsonString.substring(0, jsonEndIndex);
						}
					}

					const result = {
						rawResponse: jsonString,
						json: JSON.parse(jsonString),
						references: undefined,
						error: undefined,
					};

					return result;
				} catch (error) {
					return {
						rawResponse: (response.content[0] as TextBlock).text,
						json: undefined,
						references: undefined,
						error: undefined,
					} as AIAPIResponse;
				}
			}),
			catchError((error) => {
				console.error('Error calling Claude API:', error);
				return of({
					rawResponse: error?.message ? error.message : error,
					json: undefined,
					references: undefined,
					error: undefined,
				});
			}),
		);
	}
}
